import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { useAuth } from '../../store/AuthContext';
import { 
  Palette, 
  Layers, 
  Box, 
  Save, 
  X, 
  Loader2,
  MapPin,
  Circle,
  Crosshair,
  Store,
  Utensils,
  ShoppingBag,
  ShoppingCart,
  Film,
  Triangle,
  Trash2
} from 'lucide-react';

const BrandSettingsModal = ({ isOpen, onClose, brand, currentType, currentColor, currentIcon, currentLetter, onUpdate }) => {
  const { token, user, setUser } = useAuth();
  const [type, setType] = useState('base');
  const [color, setColor] = useState('#FF0000');
  const [icon, setIcon] = useState('pushpin');
  const [letter, setLetter] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setType(currentType || 'base');
      setColor(currentColor || '#FF0000');
      setIcon(currentIcon || 'pushpin');
      setLetter(currentLetter || '');
    }
  }, [isOpen, currentType, currentColor, currentIcon, currentLetter]);

  if (!isOpen) return null;

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar la marca "${brand}" y todos sus puntos? Esta acción no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${getApiUrl()}/base/brand/${encodeURIComponent(brand)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove brand from missionProfile so it doesn't reappear in the sidebar
      if (user?.missionProfile) {
        const brandLower = brand.toLowerCase();
        const normalize = (arr = []) => arr.filter(item => {
          const n = typeof item === 'string' ? item : item?.name || '';
          return n.toLowerCase() !== brandLower;
        });
        const updatedProfile = {
          ...user.missionProfile,
          brands: normalize(user.missionProfile.brands),
          competence: normalize(user.missionProfile.competence),
          engines: normalize(user.missionProfile.engines),
        };
        try {
          const resp = await axios.put(`${getApiUrl()}/auth/profile`, {
            missionProfile: updatedProfile
          }, { headers: { Authorization: `Bearer ${token}` } });
          setUser(resp.data.data.user);
        } catch {
          // Profile update failed silently; local state still updated
          setUser({ ...user, missionProfile: updatedProfile });
        }
      }

      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      alert('Error al eliminar la marca: ' + (err.response?.data?.message || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(`${getApiUrl()}/base/settings/${encodeURIComponent(brand)}`, {
        type,
        color,
        icon,
        letter
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      alert('Failed to update brand visuals: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const icons = [
    { id: 'pushpin', label: 'Pin', icon: MapPin },
    { id: 'dot', label: 'Dot', icon: Circle },
    { id: 'target', label: 'Radar', icon: Crosshair },
    { id: 'store', label: 'Retail', icon: Store },
    { id: 'food', label: 'Food', icon: Utensils },
    { id: 'shopping', label: 'Mall', icon: ShoppingBag },
    { id: 'supermarket', label: 'Market', icon: ShoppingCart },
    { id: 'cinema', label: 'Cinema', icon: Film },
    { id: 'hazard', label: 'Alert', icon: Triangle }
  ];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(5, 7, 10, 0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '16px',
      overflowY: 'auto',
      zIndex: 2500,
      backdropFilter: 'blur(5px)'
    }}>
      <div style={{
        backgroundColor: '#1A1A1A',
        border: '1px solid #0B3D91',
        padding: '30px',
        width: '100%',
        maxWidth: '400px',
        maxHeight: 'calc(100dvh - 32px)',
        overflowY: 'auto',
        borderRadius: '4px',
        boxShadow: '0 0 30px rgba(0, 206, 201, 0.2)',
        position: 'relative'
      }}>
        <button onClick={onClose} style={{
          position: 'absolute',
          right: '15px',
          top: '15px',
          background: 'none',
          border: 'none',
          color: '#888',
          cursor: 'pointer'
        }}>
          <X size={20} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
          <Palette color="#00CEC9" />
          <h2 style={{ color: '#F0F0F0', letterSpacing: '2px', fontSize: '1.1rem', margin: 0, textTransform: 'uppercase' }}>
            Visuals: {brand}
          </h2>
        </div>

        <form onSubmit={handleSave}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888', fontSize: '0.7rem', marginBottom: '8px' }}>
              <Layers size={14} /> SYSTEM CATEGORY (TYPE)
            </label>
            <select 
              className="nasa-input"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="base">BASE (MY SITES)</option>
              <option value="engine">ENGINE (ANALYSIS)</option>
              <option value="competence">COMPETENCE (SCRAPED)</option>
            </select>
            <p style={{ fontSize: '0.6rem', color: '#555', marginTop: '5px' }}>
              Moving the brand will re-categorize all associated telemetry points.
            </p>
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '12px', letterSpacing: '1px' }}>
              SELECT MISSION ICON
            </label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '10px',
              maxHeight: '200px',
              overflowY: 'auto',
              padding: '5px'
            }}>
              {icons.map(i => {
                const IconComponent = i.icon;
                const isSelected = icon === i.id;
                return (
                  <div 
                    key={i.id}
                    onClick={() => setIcon(i.id)}
                    style={{
                      padding: '10px',
                      backgroundColor: isSelected ? 'rgba(0, 206, 201, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                      border: isSelected ? '1px solid #00CEC9' : '1px solid transparent',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '5px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <IconComponent size={20} color={isSelected ? '#00CEC9' : '#888'} />
                    <span style={{ fontSize: '0.6rem', color: isSelected ? '#00CEC9' : '#888', textAlign: 'center' }}>
                      {i.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '8px' }}>TELEMETRY COLOR</label>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '4px' }}>
              <input 
                type="color" 
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{
                  width: '50px',
                  height: '30px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '0.9rem', color: '#00CEC9', fontFamily: 'monospace', fontWeight: 'bold' }}>
                {color.toUpperCase()}
              </span>
            </div>
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '8px', letterSpacing: '1px' }}>
              LETRA DENTRO DEL CÍRCULO
            </label>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '4px' }}>
              {/* Live preview of the circle marker */}
              <svg width="40" height="40" viewBox="0 0 40 40" style={{ flexShrink: 0 }}>
                <circle cx="20" cy="20" r="16" fill={color} stroke="#ffffff" strokeWidth="3" />
                {letter.trim() && (
                  <text x="20" y="26" textAnchor="middle" fill="#ffffff" fontSize="18" fontFamily="Arial, sans-serif" fontWeight="bold">
                    {letter.trim()}
                  </text>
                )}
              </svg>
              <input
                type="text"
                value={letter}
                maxLength={2}
                placeholder="p. ej. M"
                onChange={(e) => setLetter(e.target.value.slice(0, 2))}
                className="nasa-input"
                style={{ width: '90px', textAlign: 'center', fontSize: '1rem', fontWeight: 'bold', letterSpacing: '1px' }}
              />
              <span style={{ fontSize: '0.6rem', color: '#555', lineHeight: 1.4 }}>
                1–2 caracteres. Se muestran en el mapa y en la exportación a Google My Maps. Déjalo vacío para usar la inicial por defecto.
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || deleting}
            className="nasa-button"
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {loading ? 'SYNCHRONIZING...' : 'UPDATE MISSION VISUALS'}
          </button>

          <button
            type="button"
            onClick={handleDelete}
            disabled={loading || deleting}
            style={{
              width: '100%',
              marginTop: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '10px',
              backgroundColor: 'transparent',
              border: '1px solid #D63031',
              color: '#D63031',
              cursor: 'pointer',
              fontSize: '0.75rem',
              letterSpacing: '1px',
              borderRadius: '4px',
              opacity: (loading || deleting) ? 0.5 : 1
            }}
          >
            {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            {deleting ? 'ELIMINANDO...' : 'DELETE BRAND'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BrandSettingsModal;
